import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { useIsFeatureFlagEnabled } from "@circle-react/hooks/UseCommunitySetting";
import { useEmailHubBilling } from "@circle-react/hooks/useEmailHubBilling";
import { useIsEmailHubStatusEnabled } from "@circle-react/hooks/useIsEmailHubStatus";

import { t } from "@/i18n-js/instance";
import { EmailHubFreePeriodBanner } from "@/react/components/shared/EmailHubFreePeriodBanner";

import { NavGroup } from "../NavGroup";
import { FooterWrapper } from "./FooterWrapper";
import { SubNavLinkItem } from "./SubNavLinkItem";
import type { Link } from "./types";

const i18nRoot = "settings_nav_v3.emails";

export const useEmailLinks = (): Link[] => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isEmailTemplatesNavEnabled = useIsFeatureFlagEnabled(
    "email_hub_email_templates_nav_enabled",
  );
  const isEmailHubStatusEnabled = useIsEmailHubStatusEnabled();

  const links: Link[] = [
    {
      label: t([i18nRoot, "overview"]),
      path: settingsNavPathV3.email.overview,
      exact: true,
      isDisabled: !isAdmin,
    },
    {
      label: t([i18nRoot, "broadcasts"]),
      path: settingsNavPathV3.email.broadcast,
      exact: true,
      isDisabled: !isAdmin,
    },
    {
      label: t([i18nRoot, "forms"]),
      path: settingsNavPathV3.email.forms,
      exact: false,
      isDisabled: !isAdmin,
    },
    {
      label: t([i18nRoot, "templates"]),
      path: settingsNavPathV3.email.templates,
      exact: false,
      isDisabled: !isAdmin || !isEmailTemplatesNavEnabled,
    },
    {
      label: t([i18nRoot, "settings"]),
      path: settingsNavPathV3.email.settings,
      isDisabled: !isEmailHubStatusEnabled || !isAdmin,
      exact: true,
    },
  ].filter(Boolean) as Link[];

  return links;
};

export const EmailLinks = () => {
  const { isInFreePeriod } = useEmailHubBilling();
  const links = useEmailLinks();

  return (
    <NavGroup groupLinks={links}>
      <NavGroup.Content title={t([i18nRoot, "title"])}>
        {links.map(link => (
          <SubNavLinkItem
            key={link.path}
            label={link.label}
            to={link.path}
            isDisabled={link.isDisabled}
          />
        ))}
        {isInFreePeriod && (
          <FooterWrapper>
            <EmailHubFreePeriodBanner />
          </FooterWrapper>
        )}
      </NavGroup.Content>
    </NavGroup>
  );
};
